/*
 * @Author: AI Assistant
 * @Date: 2025-01-27
 * @Description: AI图片查看器组件 - 支持全屏、旋转、缩放、水印、下载等功能
 */

import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react'
import { Modal, Button, message } from 'antd'
import ImageSelectionEditor, {
  XCImageSelectionEditorRef
} from '../../components/ImageSelectionEditor'
import useImage from 'use-image'
import { calculateImageDisplaySize } from '../../../utils/imageUtils'
import { useSensorTracking } from '../hooks/useSensorTracking'
import './AIImageViewer.scss'
import { CloseCircleOutlined } from '@ant-design/icons'

interface XCWatermark {
  name: string
  value: string
}

interface XCSelectionInfo {
  x: number
  y: number
  width: number
  height: number
}

interface XCAIImageViewerProps {
  visible: boolean
  photo: string
  userNickname?: string
  members?: any // Immutable List
  watermarks?: XCWatermark[]
  supportSelection?: boolean // 是否支持图片框选，默认为true
  /** bbox 数据，格式为 [[x, y, width, height], ...] */
  bbox?: number[][]
  onClose: () => void
  onStartRecognition?: (
    photo: string,
    selectionInfo: XCSelectionInfo[],
    userNickname?: string
  ) => void
  onSelectionChange?: (selectionInfo: XCSelectionInfo[]) => void
}

/**
 * AI图片查看器组件
 * 功能：图片查看、用户信息、关闭按钮、开始识别按钮
 * 支持两种模式：
 * 1. 框选模式（supportSelection=true）：使用ImageSelectionEditor，支持区域选择和识别
 * 2. 预览模式（supportSelection=false）：直接展示图片，用于整改前照片预览等场景
 */
const AIImageViewer: React.FC<XCAIImageViewerProps> = ({
  visible,
  photo,
  userNickname,
  supportSelection = true,
  bbox,
  onClose,
  onStartRecognition,
  onSelectionChange
}) => {
  // hooks
  const { reportAISecurityPageActionClick } = useSensorTracking()

  // refs
  const imageSelectionEditorRef = useRef<XCImageSelectionEditorRef>(null)
  const imageSelectionRef = useRef<XCSelectionInfo[]>([])

  // 使用useImage hook获取图片的原始尺寸
  const [image] = useImage(photo)

  // 状态
  const [imageSize, setImageSize] = useState({ width: 800, height: 600 })

  // 将bbox转换为ImageSelectionEditor所需的initialSelections格式
  const initialSelections = useMemo(() => {
    if (!bbox || !bbox.length || !image) {
      return []
    }
    return bbox.map(([x, y, width, height]) => {
      // 计算缩放比例
      const scaleX = imageSize.width / image.width
      const scaleY = imageSize.height / image.height

      return {
        x: Math.round(x * scaleX),
        y: Math.round(y * scaleY),
        width: Math.round(width * scaleX),
        height: Math.round(height * scaleY)
      }
    })
  }, [bbox, image, imageSize])

  // 关闭处理
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // 开始识别处理
  const handleStartRecognition = useCallback(() => {
    if (supportSelection && !imageSelectionRef.current.length) {
      message.error('请先选择区域')
      return
    }

    // 埋点：框选后点击识别
    reportAISecurityPageActionClick({
      actionType: 'clickCropPhotoIdentify',
      picUrl: photo
    })

    if (photo && onStartRecognition) {
      // 如果不支持框选，传递空数组作为选区信息
      const selectionInfo = supportSelection ? imageSelectionRef.current : []
      onStartRecognition(photo, selectionInfo, userNickname)
      handleClose()
    }
  }, [
    photo,
    supportSelection,
    onStartRecognition,
    handleClose,
    reportAISecurityPageActionClick
  ])

  // 选区变化处理
  const handleSelectionChange = useCallback(
    (selectionInfo: XCSelectionInfo[]) => {
      imageSelectionRef.current = selectionInfo
      if (onSelectionChange) {
        onSelectionChange(selectionInfo)
      }
    },
    [onSelectionChange]
  )

  // 计算图片显示尺寸
  useEffect(() => {
    if (!image) {
      // 如果图片还未加载，使用默认尺寸
      setImageSize({ width: 800, height: 600 })
      return
    }

    // 获取可用空间，考虑右侧信息面板的宽度
    const availableWidth = supportSelection
      ? window.innerWidth - 400
      : window.innerWidth - 100 // 减去右侧面板宽度或边距
    const availableHeight = window.innerHeight - 100 // 减去顶部导航栏高度

    // 使用calculateImageDisplaySize函数计算合适的显示尺寸
    const calculatedSize = calculateImageDisplaySize(
      { width: image.width, height: image.height },
      Math.min(availableWidth, 1000),
      Math.min(availableHeight, 700)
    )

    setImageSize(calculatedSize)
  }, [visible, image, supportSelection])

  // 键盘事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!visible) return

      switch (e.keyCode) {
        case 27: // ESC
          handleClose()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [visible, handleClose])

  // 渲染内容
  const renderContent = () => {
    if (!photo) return null

    return (
      <div className="ai-image-viewer">
        <div
          className={`viewer-panel ${!supportSelection ? 'full-screen' : ''}`}
        >
          {/* 顶部导航栏 */}
          <div className="headerBackGround">
            {/* <div className="authorBox" onClick={}>
              <img src={member?.get('headimgurl')} alt="" />
              <span>{member?.get('nickname')}</span>
            </div> */}
            <div className="header" onClick={handleClose}>
              <CloseCircleOutlined className="close" />
              <span>关闭</span>
            </div>
          </div>

          {/* 主内容区 */}
          <div className="viewer-content">
            <div className="photo">
              <div>
                {supportSelection ? (
                  /* 图片编辑器 - 支持框选 */
                  <ImageSelectionEditor
                    ref={imageSelectionEditorRef}
                    src={photo}
                    width={imageSize.width}
                    height={imageSize.height}
                    hasSelection={Boolean(initialSelections.length)}
                    initialSelections={initialSelections}
                    selectionType="multiple"
                    onSelectionChange={handleSelectionChange}
                  />
                ) : (
                  /* 普通图片展示 - 不支持框选 */
                  <img
                    src={photo}
                    alt="预览图片"
                    style={{
                      maxWidth: imageSize.width,
                      maxHeight: imageSize.height,
                      objectFit: 'contain'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 右侧信息面板 - 默认展示 */}
        {supportSelection && (
          <div className="info-panel">
            <div className="info-content">
              {/* 开始识别按钮 - 仅在支持框选时显示 */}

              <div className="recognition-section">
                <Button
                  type="primary"
                  size="large"
                  className="start-recognition-btn"
                  onClick={handleStartRecognition}
                  disabled={!onStartRecognition}
                >
                  开始识别
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="100vw"
      style={{ top: 0, padding: 0 }}
      styles={{ body: { padding: 0, height: '100vh' } }}
      className="ai-image-viewer-modal"
      destroyOnHidden
      mask={{ closable: false }}
    >
      {renderContent()}
    </Modal>
  )
}

export default AIImageViewer
